import { UseTellescopeQueryOptions } from "../types";
import { useTellescopeQuery } from "../use-tellescope-query";

export interface TwilioLoadTFNEmbedResponse {
  /** Twilio TrustHub compliance inquiry id — consumed by the TFN verification embed. */
  inquiryId?: string;
  /** Short-lived session token for the embed iframe. Do not cache; request immediately before rendering. */
  token: string;
  phoneNumberSid?: string;
  /** Used by the business-profile embed variant, not TFN verification. */
  id?: string;
}

export interface UseTwilioLoadTFNEmbedQueryOptions<TError = Error, TData = TwilioLoadTFNEmbedResponse>
  extends Omit<
    UseTellescopeQueryOptions<"organizations", "load_twilio_embed", TError, TData>,
    "model" | "method" | "request"
  > {
  /** The Twilio SID (`PN…`) of the toll-free number to verify. The query is disabled until this is set. */
  phoneNumberSid: string | undefined;
}

/**
 * Initializes a Twilio TrustHub toll-free verification inquiry for a phone number and returns the
 * `{ inquiryId, token }` pair needed to render the Twilio-hosted compliance embed iframe.
 *
 * The returned `token` is short-lived, so this is configured with `staleTime`/`gcTime` of `0` — each
 * time it is enabled it fetches a fresh token. Gate it with `enabled` (and `phoneNumberSid`) so it only
 * runs when the user is actively launching the embed, then build the iframe URL from the result.
 */
export const useTwilioLoadTFNEmbedQuery = <TError = Error, TData = TwilioLoadTFNEmbedResponse>({
  phoneNumberSid,
  ...options
}: UseTwilioLoadTFNEmbedQueryOptions<TError, TData>) =>
  useTellescopeQuery({
    model: "organizations",
    method: "load_twilio_embed",
    request: { type: "tfn-verification", phoneNumberSid },
    staleTime: 0,
    gcTime: 0,
    ...options,
    enabled: !!phoneNumberSid && (options.enabled ?? true),
  });
